import { css } from 'styled-components';

/**
 * 관리자 페이지 공용 디자인 토큰 — 미니멀 플랫.
 *
 * 원칙
 * - 표면은 순수 화이트 1종 + 극연회색 1종(surfaceMuted)뿐이다. 상태 표현은 배경 틴트가
 *   아니라 1px 경계선·타이포 색 농도·마크(currentColor)로 한다.
 * - 모서리는 직각이다. 반경은 스위치 트랙(999px)·노브/도트(50%)처럼 원형 UI에만 쓴다.
 * - 그림자·그라데이션을 쓰지 않는다. 포커스 링도 그림자가 아니라 outline으로 그린다.
 * - 색 리터럴은 이 파일에서만 정의한다 — 페이지 파일은 토큰만 참조한다.
 */

export const color = {
    // 표면 (2종)
    surface: '#ffffff',
    surfaceMuted: '#f5f6f7',
    scrim: 'rgba(255, 255, 255, 0.92)',

    // 경계선
    line: '#e3e5e8',
    lineStrong: '#c8ccd1',

    // 타이포 (농도 4단)
    textStrong: '#15181c',
    text: '#34383e',
    textMuted: '#69707a',
    textFaint: '#8d939c',

    // 액센트 (상단 메뉴 #007bff와 같은 계열의 블루 1종)
    accent: '#0b63d6',
    accentStrong: '#094ea9',
    onAccent: '#ffffff',

    // 시맨틱 (각 1종 — 배경이 아니라 경계선·텍스트·마크에 쓴다)
    success: '#12704b',
    warning: '#8a5a12',
    danger: '#b3261e'
};

const STATUS_COLOR = {
    live: color.success,
    scheduled: color.accent,
    expired: color.warning,
    disabled: color.textMuted,
    incomplete: color.danger,
    neutral: color.accent,
    warning: color.warning,
    ended: color.textMuted
};

/** 팝업 노출 상태·이벤트 상태 톤을 단일 팔레트로 매핑한다. */
export const toneColor = (tone) => STATUS_COLOR[tone] || color.textMuted;

/** 타이포 위계 5단 + 보조 2종. 크기·굵기·자간으로만 층위를 만든다. */
export const font = {
    pageTitle: css`
        font-size: 28px;
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: -0.022em;
    `,
    sectionTitle: css`
        font-size: 16px;
        font-weight: 700;
        line-height: 1.4;
        letter-spacing: -0.012em;
    `,
    label: css`
        font-size: 13px;
        font-weight: 600;
        line-height: 1.45;
    `,
    body: css`
        font-size: 14px;
        font-weight: 400;
        line-height: 1.65;
    `,
    caption: css`
        font-size: 12px;
        font-weight: 400;
        line-height: 1.55;
    `,
    eyebrow: css`
        font-size: 11px;
        font-weight: 700;
        line-height: 1.4;
        letter-spacing: 0.1em;
    `,
    mono: css`
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
        font-variant-numeric: tabular-nums;
    `
};

/** 간격 리듬 — 4의 배수. */
export const space = {
    xs: '4px',
    sm: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    xxl: '32px',
    xxxl: '48px'
};

export const layout = {
    maxWidth: '1240px',
    pagePadding: '36px clamp(20px, 4vw, 56px) 72px',
    pagePaddingSmall: '24px 16px 48px'
};

export const mixin = {
    /** 화이트 표면 + 1px 헤어라인. 카드는 그림자가 아니라 경계선으로 선다. */
    panel: css`
        background: ${color.surface};
        border: 1px solid ${color.line};
    `,
    hairlineTop: css`
        border-top: 1px solid ${color.line};
    `,
    focusRing: css`
        &:focus-visible {
            outline: 2px solid ${color.accent};
            outline-offset: 2px;
        }
    `,
    /** 입력 요소 공통 — 직각·1px 경계선·outline 포커스. */
    field: css`
        box-sizing: border-box;
        width: 100%;
        min-height: 44px;
        padding: 0 12px;
        border: 1px solid ${color.lineStrong};
        background: ${color.surface};
        color: ${color.text};
        font: inherit;
        font-size: 14px;

        &::placeholder {
            color: ${color.textFaint};
        }

        &:hover:not(:disabled) {
            border-color: ${color.textFaint};
        }

        &:focus {
            outline: 2px solid ${color.accent};
            outline-offset: -1px;
            border-color: ${color.accent};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
            cursor: not-allowed;
        }
    `,
    buttonBase: css`
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 40px;
        padding: 0 16px;
        border: 1px solid transparent;
        font-size: 13px;
        font-weight: 700;
        line-height: 1;
        cursor: pointer;

        &:focus-visible {
            outline: 2px solid ${color.accent};
            outline-offset: 2px;
        }

        &:disabled {
            cursor: not-allowed;
        }
    `,
    buttonPrimary: css`
        border-color: ${color.accent};
        background: ${color.accent};
        color: ${color.onAccent};

        &:hover:not(:disabled) {
            border-color: ${color.accentStrong};
            background: ${color.accentStrong};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    buttonGhost: css`
        border-color: ${color.lineStrong};
        background: ${color.surface};
        color: ${color.text};

        &:hover:not(:disabled) {
            border-color: ${color.textMuted};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    buttonDanger: css`
        border-color: ${color.lineStrong};
        background: ${color.surface};
        color: ${color.danger};

        &:hover:not(:disabled) {
            border-color: ${color.danger};
        }

        &:disabled {
            border-color: ${color.line};
            background: ${color.surfaceMuted};
            color: ${color.textFaint};
        }
    `,
    /** 상태 칩 — 배경 틴트 없이 1px 경계선 + 타이포 색으로만 구분한다. */
    statusChip: css`
        display: inline-flex;
        align-items: center;
        gap: 6px;
        flex: 0 0 auto;
        padding: 4px 8px;
        border: 1px solid ${color.line};
        background: ${color.surface};
        font-size: 11px;
        font-weight: 700;
        line-height: 1.3;
        white-space: nowrap;
    `,
    /** 상태 마크(도트) — 색 리터럴 없이 부모의 currentColor를 쓴다. */
    dot: css`
        flex: 0 0 auto;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    `,
    /** 스위치 트랙·노브 — 유일한 원형 예외. */
    switchTrack: css`
        position: relative;
        flex: 0 0 auto;
        width: 46px;
        height: 26px;
        padding: 0;
        border: 1px solid transparent;
        border-radius: 999px;
        cursor: pointer;
        transition: background 140ms ease, border-color 140ms ease;
    `,
    switchKnob: css`
        position: absolute;
        top: 3px;
        width: 18px;
        height: 18px;
        border-radius: 50%;
        background: ${color.surface};
        transition: left 140ms ease;
    `,
    /** 표 공통 — 헤더만 극연회색, 행 구분은 헤어라인. */
    table: css`
        width: 100%;
        border-collapse: collapse;
        color: ${color.text};
        font-size: 12px;

        th,
        td {
            padding: 10px 12px;
            border-bottom: 1px solid ${color.line};
            text-align: left;
            white-space: nowrap;
        }

        th {
            background: ${color.surfaceMuted};
            color: ${color.textMuted};
            font-size: 11px;
            font-weight: 700;
            letter-spacing: 0.02em;
        }

        tbody tr:last-child td {
            border-bottom: 0;
        }
    `
};
